import Phaser from 'phaser';
import { goalsScore } from '../rankText';
import { MOWERS, type MowerId } from '../../content/mowers';
import { ENDLESS_MODES, type EndlessMode } from '../../gen/modes';
import {
  campaignScope,
  dailyScope,
  endlessBoard,
  FEATURED,
  isoWeek,
  goalsBoard,
  isSoccerLawn,
  lawnBoard,
  STREAK_CURRENT,
  STREAK_LONGEST,
  topPercent,
} from '../../meta/online/boards';
import type { BoardResponse, Featured, GhostResponse } from '../../meta/online/schema';
import { TICK_RATE } from '../../sim/constants';
import { onAppShown } from '../../platform/native';
import { featuredNow, loadBoard, loadFeatured, loadGhost, onlineOn } from '../online';
import { saves, todayKey } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { drawClubBar } from '../ui/clubChrome';
import { drawHeader, drawTabBar, TABBAR_H } from '../ui/navChrome';
import { label, UI } from '../ui/text';
import { formatTime } from '../ui/widgets';
import { raceDailyLeader } from './dailyRace';
import { playFeatured } from './featuredPlay';
import { wipeTo } from './WipeScene';
import { whoText } from '../nicknames';

/** One board the screen can show. `note` fits between the arrows: 22 characters at most. */
interface Choice {
  /** Which kind, for its buttons: the Daily and the featured lawn can be played from here. */
  kind: 'daily' | 'featured' | 'lawn' | 'other';
  title: string;
  note: string;
  board: string;
  scope: string;
  format: 'time' | 'days' | 'score' | 'goals';
}

/** Which board to open on (otherwise the one last looked at, or today's Daily). */
export interface BoardsData {
  /**
   * `daily`, `streak`, `featured` (this week's featured lawn), an Endless mode (its weekly board),
   * or a campaign lawn's board for one mower (from its results).
   */
  open?: 'daily' | 'streak' | 'featured' | EndlessMode | { lawn: number; mower: MowerId };
}

/** The board last looked at this session, which CLUB > BOARDS opens on. */
let lastBoard = 0;
/** Everyone, or only the player and their friends (kept for the session). */
let friendsOnly = false;

function choices(
  nowMs: number,
  featured: Featured | null,
  lawn: { lawn: number; mower: MowerId } | null,
): Choice[] {
  const week = `week:${isoWeek(nowMs)}`;
  const lawnScope = lawn ? campaignScope(lawn.lawn, lawn.mower) : null;
  return [
    {
      kind: 'daily',
      title: 'DAILY',
      note: 'Fastest time today',
      board: 'daily',
      scope: dailyScope(todayKey(new Date(nowMs))),
      format: 'time',
    },
    ...(featured
      ? [
          {
            kind: 'featured' as const,
            title: 'WEEKLY',
            note: `Lawn ${featured.lawn}, any mower`,
            board: FEATURED,
            scope: featured.scope,
            format: 'time' as const,
          },
        ]
      : []),
    // A campaign lawn's board, opened from its results (not one of the boards flipped through
    // otherwise: there are hundreds).
    ...(lawn && lawnScope
      ? [
          {
            kind: 'lawn' as const,
            title: `LAWN ${lawn.lawn}`,
            note: MOWERS[lawn.mower].name,
            // Soccer plays to the whistle: its board is goals, then grass mowed.
            ...(isSoccerLawn(lawn.lawn)
              ? { board: goalsBoard(lawn.lawn, lawn.mower), format: 'goals' as const }
              : { board: lawnBoard(lawn.lawn, lawn.mower), format: 'time' as const }),
            scope: lawnScope,
          },
        ]
      : []),
    {
      kind: 'other',
      title: 'STREAK',
      note: 'Days in a row now',
      board: STREAK_CURRENT,
      scope: 'all',
      format: 'days',
    },
    {
      kind: 'other',
      title: 'BEST STREAK',
      note: 'Most days in a row',
      board: STREAK_LONGEST,
      scope: 'all',
      format: 'days',
    },
    ...ENDLESS_MODES.flatMap((m): Choice[] => [
      {
        kind: 'other',
        title: m.name.toUpperCase(),
        note: 'Best score this week',
        board: endlessBoard(m.id),
        scope: week,
        format: 'score',
      },
      {
        kind: 'other',
        title: m.name.toUpperCase(),
        note: 'Best score, all time',
        board: endlessBoard(m.id),
        scope: 'all',
        format: 'score',
      },
    ]),
  ];
}

const formatValue = (value: number, format: Choice['format']): string => {
  if (format === 'time') return formatTime(value, TICK_RATE);
  if (format === 'goals') return goalsScore(value);
  return String(value);
};

const ago = (ms: number): string => {
  const mins = Math.floor((Date.now() - ms) / 60_000);
  return mins < 1 ? 'Updated just now' : `Updated ${mins} min ago`;
};

/** How long until a week ends, as "2d 5h" (or "5h 12m" on its last day). */
function endsIn(ms: number): string {
  const mins = Math.max(0, Math.ceil((ms - Date.now()) / 60_000));
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  return days > 0 ? `${days}d ${hours}h` : `${hours}h ${mins % 60}m`;
}

/** A small gold crown (last week's featured winner), 5 by 4 pixels, its top left at x, y. */
function drawCrown(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(UI.shadow).fillRect(x + 1, y + 2, 5, 3);
  g.fillStyle(0xffd23f)
    .fillRect(x, y, 1, 1)
    .fillRect(x + 2, y, 1, 1)
    .fillRect(x + 4, y, 1, 1)
    .fillRect(x, y + 1, 5, 3);
}

/**
 * CLUB > BOARDS (docs/ONLINE.md, "Boards"; docs/ONLINE_UI.md): today's Daily, the week's featured
 * lawn, the streaks, and every Endless mode this week and of all time, flipped through with the
 * arrows (and a campaign lawn's board, opened from its results). Everyone or only friends. The top
 * 10, the player's own row and the rows around it, and their "top X%". Reads the server when it
 * can, and says so when it can't.
 */
export class BoardsScene extends Phaser.Scene {
  private list: Choice[] = [];
  private index = 0;
  private board: BoardResponse | null = null;
  /** The leader's run, to race (the Daily and the featured lawn). */
  private ghost: GhostResponse | null = null;
  private loading = false;
  private failed = false;
  /** Drops answers to boards the player has already moved past. */
  private asked = 0;
  private lawn: { lawn: number; mower: MowerId } | null = null;
  /** Opened on the featured board, before the server said which lawn it is. */
  private wantFeatured = false;
  /** When the board on screen was last asked for (a quick trip away does not ask again). */
  private fetchedAt = 0;

  constructor() {
    super('Boards');
  }

  init(data: BoardsData = {}): void {
    // Opened on a board once (Phaser would start the scene with it again from the CLUB bar).
    this.sys.settings.data = {};
    const open = data.open;
    this.wantFeatured = open === 'featured';
    this.lawn = typeof open === 'object' ? open : null;
    this.list = choices(Date.now(), featuredNow(), this.lawn);
    this.index = open ? this.find(open) : Math.min(lastBoard, this.list.length - 1);
    lastBoard = this.index;
    this.board = null;
    this.ghost = null;
    this.loading = false;
    this.failed = false;
  }

  /** The list's index of the board to open on (the Daily if it is not there). */
  private find(open: NonNullable<BoardsData['open']>): number {
    return Math.max(
      0,
      this.list.findIndex((c) =>
        typeof open === 'object'
          ? c.kind === 'lawn'
          : open === 'daily' || open === 'featured'
            ? c.kind === open
            : open === 'streak'
              ? c.board === STREAK_CURRENT
              : c.board === endlessBoard(open),
      ),
    );
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    void this.fetchBoard();
    this.joinFeatured(this.wantFeatured);
    // Back after a while (owner, 2026-10-31): the board read again, and the day's or week's own.
    const stop = onAppShown(this.refresh);
    this.events.once('shutdown', stop);
  }

  /**
   * The app back from the background: the boards worked out again (the day or the week may have
   * turned, which gives the Daily and Endless boards new scopes, and the featured lawn may have
   * ended), on the same board, read afresh unless it was read moments ago.
   */
  private readonly refresh = (): void => {
    if (!this.scene.isActive()) return;
    const current = this.list[this.index]!;
    this.list = choices(Date.now(), featuredNow(), this.lawn);
    const same = this.list.findIndex(
      (c) =>
        c.kind === current.kind &&
        (c.kind === 'daily' ||
          c.kind === 'featured' ||
          (c.board === current.board && c.note === current.note)),
    );
    this.index = Math.max(0, same);
    lastBoard = this.index;
    const now = this.list[this.index]!;
    const moved = now.board !== current.board || now.scope !== current.scope;
    if (moved) {
      this.board = null;
      this.ghost = null;
    }
    this.joinFeatured(false);
    if (moved || Date.now() - this.fetchedAt > 5000) void this.fetchBoard();
    else this.redraw();
  };

  /** The featured lawn joins the list once the server has said which it is. */
  private joinFeatured(wantFeatured: boolean): void {
    if (!featuredNow() && onlineOn()) {
      void loadFeatured().then((featured) => {
        if (!featured || !this.scene.isActive()) return;
        const current = this.list[this.index]!;
        this.list = choices(Date.now(), featured, this.lawn);
        if (wantFeatured) {
          this.index = this.find('featured');
          lastBoard = this.index;
          this.board = null;
          void this.fetchBoard();
        } else {
          this.index = Math.max(
            0,
            this.list.findIndex((c) => c.board === current.board && c.scope === current.scope),
          );
          lastBoard = this.index;
          this.redraw();
        }
      });
    }
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private async fetchBoard(): Promise<void> {
    if (!onlineOn()) return;
    const choice = this.list[this.index]!;
    const asked = ++this.asked;
    this.fetchedAt = Date.now();
    this.loading = true;
    this.failed = false;
    this.redraw();
    const races = choice.kind === 'daily' || choice.kind === 'featured';
    const [board, ghost] = await Promise.all([
      loadBoard(choice.board, choice.scope, { friends: friendsOnly }),
      races ? loadGhost(choice.board, choice.scope) : Promise.resolve(null),
    ]);
    if (asked !== this.asked) return;
    this.loading = false;
    this.board = board;
    this.ghost = ghost;
    this.failed = board === null;
    this.redraw();
  }

  private move(step: 1 | -1): void {
    this.index = (this.index + step + this.list.length) % this.list.length;
    lastBoard = this.index;
    this.board = null;
    this.ghost = null;
    void this.fetchBoard();
  }

  private setFriends(on: boolean): void {
    if (friendsOnly === on) return;
    friendsOnly = on;
    this.board = null;
    void this.fetchBoard();
  }

  private draw(): void {
    drawHeader(this, 'CLUB', 'club');
    drawTabBar(this, 'club');
    this.drawBoard(drawClubBar(this, 'boards') + 4);
  }

  /** The picker and the board, from `pickY` down to the tab bar. */
  private drawBoard(pickY: number): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    const backY = height - TABBAR_H;

    if (!onlineOn()) {
      label(this, width / 2, pickY + 12, 'Online scores are off.', 1, UI.accent).setOrigin(0.5, 0);
      label(this, width / 2, pickY + 26, 'Turn them on in Settings,', 1, UI.dim).setOrigin(0.5, 0);
      label(this, width / 2, pickY + 36, 'Online.', 1, UI.dim).setOrigin(0.5, 0);
      bevelButton(this, 6, pickY + 54, width - 12, 20, 'ONLINE SETTINGS', () =>
        wipeTo(this, 'Online'),
      );
      return;
    }

    // The board picker, then everyone or friends.
    const choice = this.list[this.index]!;
    bevelButton(this, 6, pickY, 22, 18, '<', () => this.move(-1));
    bevelButton(this, width - 28, pickY, 22, 18, '>', () => this.move(1));
    label(this, width / 2, pickY + 1, choice.title, 1, UI.accent).setOrigin(0.5, 0);
    label(this, width / 2, pickY + 10, choice.note, 1, UI.dim).setOrigin(0.5, 0);
    const toggleY = pickY + 22;
    const half = Math.floor((width - 16) / 2);
    bevelButton(this, 6, toggleY, half, 14, 'EVERYONE', () => this.setFriends(false), !friendsOnly);
    bevelButton(
      this,
      10 + half,
      toggleY,
      half,
      14,
      'FRIENDS',
      () => this.setFriends(true),
      friendsOnly,
    );

    const top = toggleY + 20;
    // The Daily's and the featured lawn's buttons take a row over the footer.
    const featured = choice.kind === 'featured' ? featuredNow() : null;
    const ghost = choice.kind === 'daily' || choice.kind === 'featured' ? this.ghost : null;
    const footerH = ghost || featured ? 48 : 24;
    const pitch = 10;
    const rowsRoom = Math.floor((backY - 6 - footerH - top - 8) / pitch);
    this.drawFooter(backY, choice, featured, ghost);
    const board = this.board;
    const noPlayer = friendsOnly && !saves().save.online.player;
    if (!board || noPlayer) {
      const text = noPlayer
        ? 'Add friends in CLUB > YOU.'
        : this.loading
          ? 'Loading...'
          : this.failed
            ? "Can't reach the leaderboards right now."
            : '';
      label(this, width / 2, top + 20, text, 1, UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      return;
    }
    if (board.total === 0) {
      label(
        this,
        width / 2,
        top + 20,
        friendsOnly ? 'No friends here yet.' : 'No one here yet. Be the first!',
        1,
        UI.dim,
      ).setOrigin(0.5, 0);
      return;
    }

    // The top rows, then (when the player is further down) a gap and the rows around them.
    const me = board.me;
    const meInTop = !!me && board.top.some((r) => r.player === me.player);
    const aroundRoom = me && !meInTop ? Math.min(5, Math.max(1, rowsRoom - 5)) : 0;
    const topRows = board.top.slice(0, Math.max(1, rowsRoom - (aroundRoom ? aroundRoom + 1 : 0)));
    let around = board.around;
    if (me && aroundRoom) {
      const at = around.findIndex((r) => r.player === me.player);
      const from = Math.max(
        0,
        Math.min(at - Math.floor(aroundRoom / 2), around.length - aroundRoom),
      );
      around = around
        .slice(from, from + aroundRoom)
        .filter((r) => !topRows.some((t) => t.player === r.player));
    } else around = [];
    navyPanel(
      g,
      4,
      top - 4,
      width - 8,
      (topRows.length + (around.length ? around.length + 1 : 0)) * pitch + 8,
    );
    let y = top;
    const row = (r: (typeof board.top)[number]) => {
      const mine = r.player === me?.player;
      if (mine) g.fillStyle(0x3a2f12).fillRect(6, y - 1, width - 12, pitch);
      const color = mine ? UI.accent : UI.text;
      label(this, 26, y, `${r.rank}`, 1, mine ? UI.accent : UI.dim).setOrigin(1, 0);
      const name = label(this, 32, y, whoText(r.player, r.name), 1, color);
      if (r.crown) drawCrown(g, Math.round(name.x + name.width + 3), y + 1);
      label(this, width - 10, y, formatValue(r.value, choice.format), 1, color).setOrigin(1, 0);
      y += pitch;
    };
    topRows.forEach(row);
    if (around.length) {
      label(this, width / 2, y - 2, '...', 1, UI.dim).setOrigin(0.5, 0);
      y += pitch;
      around.forEach(row);
    }
    // Only the player on a friends board: how to add some.
    if (friendsOnly && board.total === 1 && y + 14 < backY - 6 - footerH) {
      label(this, width / 2, y + 6, 'Add friends in CLUB > YOU.', 1, UI.dim).setOrigin(0.5, 0);
    }
  }

  /**
   * The footer: the Daily's RACE THE LEADER, or the featured lawn's PLAY (and RACE, when someone
   * else leads), then the player's place and how fresh the board is (or when the week ends).
   */
  private drawFooter(
    backY: number,
    choice: Choice,
    featured: Featured | null,
    ghost: GhostResponse | null,
  ): void {
    const { width } = this.scale.gameSize;
    const fy = backY - 6 - 24;
    const board = this.board;
    if (featured) {
      const leader = board?.top[0];
      const race = ghost && leader && leader.player !== board?.me?.player ? ghost : null;
      const w = race ? Math.floor((width - 16) / 2) : width - 12;
      bevelButton(this, 6, fy - 26, w, 20, 'PLAY', () => playFeatured(this, featured), true);
      if (race) {
        bevelButton(this, 10 + w, fy - 26, w, 20, 'RACE LEADER', () =>
          playFeatured(this, featured, race),
        );
      }
    } else if (ghost && choice.kind === 'daily') {
      bevelButton(
        this,
        6,
        fy - 26,
        width - 12,
        20,
        'RACE THE LEADER',
        () => raceDailyLeader(this, ghost),
        true,
      );
    }
    if (!board) {
      if (featured) {
        label(this, width / 2, fy + 11, `Ends in ${endsIn(featured.ends)}`, 1, UI.dim).setOrigin(
          0.5,
          0,
        );
      }
      return;
    }
    const me = board.me;
    const place = me
      ? `You: #${me.rank} of ${board.total}, top ${topPercent(me.rank, board.total)}%`
      : `${board.total} player${board.total === 1 ? '' : 's'}`;
    label(this, width / 2, fy, place, 1, me ? UI.accent : UI.text).setOrigin(0.5, 0);
    const fresh = featured ? `Ends in ${endsIn(featured.ends)}` : ago(board.at);
    label(this, width / 2, fy + 11, fresh, 1, UI.dim).setOrigin(0.5, 0);
  }
}
